import { useRef, useMemo, useState, useEffect } from 'react';
import qs from 'qs';
import { SelectedFacetsProps } from '../state/Context';

export type QueryParams = {
  query?: string;
  page: number;
  sort?: string;
  selectedFacets: SelectedFacetsProps;
};

const useParams = (onPopState: (params: QueryParams) => void) => {
  const updateQs = useRef(false);

  // Get initial params from query string
  const { query, page, sort, selectedFacets } = useMemo(() => {
    // Parse the query string
    const { query, page, sort, ...selectedFacets } = qs.parse(location.search, {
      ignoreQueryPrefix: true
    });

    // If the value of the param is a string,
    // convert it to a single item array
    Object.keys(selectedFacets).forEach(key => {
      if (typeof selectedFacets[key] === 'string') {
        selectedFacets[key] = [selectedFacets[key] as string];
      }
    });

    return { query, page, sort, selectedFacets };
  }, []);

  const [queryParams, setQueryParams] = useState<QueryParams>({
    query: query ? `${query}` : undefined,
    page: parseInt(`${page}`) || 1,
    sort: sort ? `${sort}` : undefined,
    selectedFacets: selectedFacets as SelectedFacetsProps
  });

  const updateQueryParams = (params: QueryParams) => {
    setQueryParams(params);
  };

  useEffect(() => {
    // Build the query string and update the URL
    const buildQueryString = () => {
      const { selectedFacets, ...p } = queryParams;

      const qsValue = qs.stringify(
        { ...p, ...selectedFacets },
        {
          encode: false,
          encodeValuesOnly: true,
          arrayFormat: 'repeat'
        }
      );

      const newUrl = `${location.protocol}//${location.host}${
        location.pathname
      }${qsValue ? `?${qsValue}` : ''}`;
      history.pushState({}, '', newUrl);
    };

    if (updateQs.current) {
      buildQueryString();
    } else {
      updateQs.current = true;
    }
  }, [queryParams]);

  useEffect(() => {
    const handlePopState = () => {
      const qsParams = qs.parse(location.search, { ignoreQueryPrefix: true });
      updateQs.current = false;

      const updatedQueryParams = {
        query: qsParams.query ? `${qsParams.query}` : '',
        page: parseInt(`${qsParams.page}`) || 1,
        sort: qsParams.sort ? `${qsParams.sort}` : '',
        selectedFacets: qsParams.selectedFacets
          ? (qsParams.selectedFacets as SelectedFacetsProps)
          : {}
      };

      if (onPopState) {
        onPopState(updatedQueryParams);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return { queryParams, updateQueryParams };
};

export default useParams;
